/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, FC } from 'react'
import { injectIntl } from 'react-intl'
import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import {
  applyFullValidation,
  isProfileValid,
} from '@vtexlab/instore-profile/lib/modules/validateProfile'
import addValidation from '@vtexlab/instore-profile/lib/modules/addValidation'
import removeValidation from '@vtexlab/instore-profile/lib/modules/removeValidation'
import defaultRules from '@vtexlab/instore-profile/lib/rules/default'
import ProfileField from '@vtexlab/instore-profile/lib/ProfileField'
import Checkbox from '@vtex/styleguide/lib/Checkbox'
import InputField from './InputField'
import { ProfileShape, ProfileContainerProps, RuleFieldShape } from './types'
import PersonalForm from './PersonalForm'
import CorporateToggle from './CorporateToggle'
import CorporateForm from './CorporateForm'
import { FormFields } from './RegisterFields'
import isEmpty from 'lodash/isEmpty'

const ProfileContainer: FC<ProfileContainerProps> = ({
  rules = defaultRules,
  onChange,
  loading,
  customer,
  showCorporate,
  intl,
  mediaQuery,
  isAnonymous,
  showCorporateToggle,
  noStateInscription,
}) => {
  // validProfile prevent the setProfile to reset and  dont show if the user has invalid data
  const [validProfile, setValidProfile] = useState(false)
  const [exemptStateInscription, setExemptStateInscription] = useState(
    noStateInscription
  )
  const [corporateProfile, setCorporateProfile] = useState(showCorporate)

  const placeholder = intl.formatMessage({
    id: 'profile-form.field.exemptStateRegistration',
  })
  const updatedRulesWithExemptInscription = (exempt: boolean) => {
    return rules.businessFields
      .filter((field: RuleFieldShape) =>
        FormFields.business.includes(field.name)
      )
      .map((field: RuleFieldShape) => {
        if (field.name === 'stateRegistration') {
          return {
            ...field,
            disabled: exempt,
            required: !exempt,
            placeholder,
          }
        }
        return field
      })
  }
  const [showCorporateForm, setShowCorporateForm] = useState(showCorporate)

  const [personalRulesFields, setPersonalRulesFields] = useState(
    rules.personalFields.filter((field: RuleFieldShape) =>
      FormFields.personal.includes(field.name)
    )
  )

  const [businessRulesFields, setBusinessRulesFields] = useState(
    updatedRulesWithExemptInscription(noStateInscription)
  )
  const [profile, setProfile] = useState(
    addValidation(customer, { ...rules, businessFields: businessRulesFields })
  )
  const [validEmail, setValidEmail] = useState(false)

  const mobile = mediaQuery({
    phone: true,
    tablet: false,
    desktop: false,
  })

  useEffect(
    function setCustomer() {
      if (
        !isAnonymous &&
        !validProfile &&
        !isEmpty(customer.firstName) &&
        isEmpty(profile.firstName.value)
      ) {
        setProfile(addValidation(customer, rules))
        setShowCorporateForm(showCorporate)
        setExemptStateInscription(noStateInscription)
        setBusinessRulesFields(
          updatedRulesWithExemptInscription(noStateInscription)
        )
        setValidProfile(true)
      }
    },
    [customer]
  )

  const handleStateInscription = () => {
    setValidProfile(true)
    setExemptStateInscription(!exemptStateInscription)
    const updatedRules = updatedRulesWithExemptInscription(
      !exemptStateInscription
    )
    setBusinessRulesFields(updatedRules)
  }
  useEffect(
    function getRules() {
      if (!validProfile) {
        return
      }
      setPersonalRulesFields(
        rules.personalFields
          .filter((field: RuleFieldShape) =>
            FormFields.personal.includes(field.name)
          )
          .map((field: RuleFieldShape) => ({ ...field, disabled: loading }))
      )
      setBusinessRulesFields(
        rules.businessFields
          .filter((field: RuleFieldShape) =>
            FormFields.business.includes(field.name)
          )
          .map((field: RuleFieldShape) => ({ ...field, disabled: loading }))
      )
      if (!loading) {
        setExemptStateInscription(noStateInscription)
        setBusinessRulesFields(
          updatedRulesWithExemptInscription(noStateInscription)
        )
        setShowCorporateForm(showCorporate)
        echoProfile(addValidation(customer, rules))
      }
    },
    [loading]
  )

  useEffect(
    function updateOnBusinessRulesChange() {
      if (validProfile) {
        echoProfile(profile)
        if (validEmail) {
          setValidEmail(false)
        }
      }
    },
    [
      businessRulesFields,
      corporateProfile,
      validEmail,
      exemptStateInscription,
      showCorporateForm,
    ]
  )

  const echoProfile = (updatedProfile: ProfileShape) => {
    setProfile(updatedProfile)

    const validatedProfile = applyFullValidation(
      { ...rules, businessFields: businessRulesFields },
      updatedProfile,
      corporateProfile
    )
    const newProfile: ProfileShape = {
      ...removeValidation(validatedProfile, rules),
      corporateProfile,
    }

    const isValid = isProfileValid(validatedProfile)

    onChange({
      isValid,
      profile: newProfile,
      validateEmail: validEmail,
      showCorporate: showCorporateForm,
      noStateInscription: exemptStateInscription,
    })
  }

  const handleFieldUpdate = (field: RuleFieldShape) => {
    const updatedProfile = {
      ...profile,
      ...{ ...field, touched: true },
    }

    echoProfile(updatedProfile)
  }

  const onClick = () => {
    setValidProfile(true)
    setValidEmail(true)
  }

  const options: {
    email: {
      onClick: () => void
    }
  } = { email: { onClick } }

  const corporateUpdateShow = () => {
    setValidProfile(true)
    setCorporateProfile(!showCorporateForm)
    setShowCorporateForm(!showCorporateForm)
  }

  const createField = (field: RuleFieldShape) => {
    if (!profile) {
      return null
    }

    return (
      <div>
        <ProfileField
          data={profile[field.name]}
          field={field}
          Input={InputField}
          key={field.name}
          options={field.name === 'email' ? options.email : null}
          onFieldUpdate={handleFieldUpdate}
        />
      </div>
    )
  }

  const personalFields = personalRulesFields.reduce(
    (acc: Record<string, any>, field: RuleFieldShape) => {
      acc[field.name] = createField(field)
      return acc
    },
    {}
  )

  const businessFields = businessRulesFields.reduce(
    (acc: Record<string, any>, field: RuleFieldShape) => {
      acc[field.name] = createField(field)
      return acc
    },
    {}
  )

  return (
    <div>
      <div>
        <PersonalForm fields={personalFields} mobile={mobile} />
        <CorporateToggle
          showCorporateToggle={showCorporateToggle}
          showCorporateForm={showCorporateForm}
          corporateUpdateShow={corporateUpdateShow}
          intl={intl}
        />
        {showCorporateForm ? (
          <div>
            <CorporateForm fields={businessFields} mobile={mobile} />
            <div className="pb5" data-testid="setNoStateChange">
              <Checkbox
                id="setNoStateChange"
                name={''}
                checked={exemptStateInscription}
                onChange={handleStateInscription}
                label={intl.formatMessage({
                  id: 'profile-form.field.noStateRegistration',
                })}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
export default withMediaQuery(injectIntl(ProfileContainer))
